import { useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { CheckCircleOutlined, DatabaseOutlined, SyncOutlined, WarningOutlined } from '@ant-design/icons';

import { PageHeader } from '../../../components/shared';
import { Badge } from '../../../components/ui';
import { useDuongDan } from '../../../hooks/useDuongDan';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { LuoiNguon } from '../components/chung/LuoiNguon';
import { KHOA_HIEN } from '../constants';
import { useDanhSachNguon } from '../hooks/useChung';
import { useTrangThaiNhieuNguon } from '../hooks/useKho';
import type { GraphqlSource } from '../types';

/** Đường GỐC tới màn chi tiết một nguồn, giữ `bang`/`cot` của đường cũ (đi từ một lời từ chối sang đúng ô còn trống). */
export function khoHref(id: string, thamSo?: URLSearchParams | null): string {
  const q = new URLSearchParams();
  for (const k of ['bang', 'cot']) {
    const v = thamSo?.get(k);
    if (v) q.set(k, v);
  }
  const chuoi = q.toString();
  return `/tro-ly-ai/ban-sao/${encodeURIComponent(id)}${chuoi ? `?${chuoi}` : ''}`;
}

const trucTiep = (s: GraphqlSource) => s.kind === 'dsn' && s.accessMode === 'live';

/**
 * Kho dữ liệu (`/tro-ly-ai/ban-sao`) — lưới nguồn CSDL/GraphQL; bấm một thẻ để sang màn làm việc của nguồn ấy (port
 * `KhoDuLieuView.tsx` native).
 *
 * `?nguon=&bang=&cot=` là đường cũ từ một lời từ chối trong câu trả lời: chuyển thẳng sang màn chi tiết và GIỮ `bang`/`cot`.
 * Thiếu `nguon` thì gắn hai tham số ấy vào link của mọi thẻ, để chọn nguồn xong vẫn rơi đúng ô.
 */
export function KhoDuLieuView() {
  const { t, locale } = useT();
  const duongDan = useDuongDan();
  const [thamSo] = useSearchParams();
  const nguonTs = thamSo.get('nguon');

  const sources = useDanhSachNguon();
  // Nguồn API không có kho nào để khai hay đồng bộ — nó sống ở Truy vấn mẫu.
  const ds = useMemo(() => (sources.data ?? []).filter((s) => s.kind !== 'api'), [sources.data]);
  const statusQ = useTrangThaiNhieuNguon(ds.map((s) => s.id));
  const statusTheoId = new Map(ds.map((s, i) => [s.id, statusQ[i]]));

  if (nguonTs) return <Navigate to={duongDan(khoHref(nguonTs, thamSo))} replace />;

  const chan = (s: GraphqlSource) => {
    const q = statusTheoId.get(s.id);
    if (q?.isError) return <span>{t('troLyAiKhoView.khongDocTrangThai')}</span>;
    if (!q?.data) return undefined;
    const bang = q.data.tables;
    if (q.data.dongBo?.dangChay) {
      return (
        <>
          <SyncOutlined spin className="text-primary-600" aria-hidden />
          {t('troLyAiKhoView.dangDongBo')}
        </>
      );
    }
    if (bang.length === 0) return <span>{t('troLyAiKhoView.chuaKhaiBang')}</span>;
    const dung = bang.filter((b) => b.lastStatus === 'stopped').length;
    if (dung > 0) {
      return (
        <>
          <WarningOutlined className="text-warning-600" aria-hidden />
          {t('troLyAiKhoView.coBangDung', { n: soVN(bang.length, locale), dung: soVN(dung, locale) })}
        </>
      );
    }
    const dong = bang.reduce((a, b) => a + b.rowCount, 0);
    return (
      <>
        <CheckCircleOutlined className="text-success-600" aria-hidden />
        {trucTiep(s)
          ? t('troLyAiKhoView.demBangTrucTiep', { n: soVN(bang.length, locale) })
          : t('troLyAiKhoView.demBang', { n: soVN(bang.length, locale), dong: soVN(dong, locale) })}
      </>
    );
  };

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.troLiAi') }, { label: t('nav.muc.khoDuLieu') }]}
          titleIcon={<DatabaseOutlined className="text-primary-600" />}
          title={t('troLyAiKhoView.tieuDe')}
          subtitle={t('troLyAiKhoView.phuDe')}
        />
        <LuoiNguon
          ds={ds}
          dangTai={sources.isPending}
          loi={sources.error}
          onThuLai={() => void sources.refetch()}
          khoaHien={KHOA_HIEN.khoDuLieu}
          hrefCua={(s) => khoHref(s.id, thamSo)}
          boLoc={[
            {
              placeholder: t('troLyAi.luoiNguon.moiLoai'),
              options: [
                { value: 'dsn', label: t('troLyAi.luoiNguon.loaiCsdl'), khop: (s) => s.kind === 'dsn' },
                { value: 'graphql', label: t('troLyAi.luoiNguon.loaiGraphql'), khop: (s) => s.kind !== 'dsn' },
              ],
            },
            {
              placeholder: t('troLyAiKhoView.moiCachLay'),
              options: [
                { value: 'replica', label: t('troLyAiKhoView.banSao'), khop: (s) => !trucTiep(s) },
                { value: 'live', label: t('troLyAiKhoView.trucTiep'), khop: trucTiep },
              ],
            },
          ]}
          nhan={(s) => (
            <>
              <Badge variant="neutral">{trucTiep(s) ? t('troLyAiKhoView.trucTiep') : t('troLyAiKhoView.banSao')}</Badge>
              {s.cheDo === 'khuon' ? <Badge variant="violet">{t('troLyAiKhoView.theoKhuon')}</Badge> : null}
            </>
          )}
          chan={chan}
        />
      </div>
    </div>
  );
}
